/**
 * New-mail notifications, on this device only.
 *
 * In the desktop app (ADR-0035) the page may call exactly one native thing:
 * the notification plugin, granted to the configured server's origin and
 * nothing else. In a browser, the Web Notifications API. Either way the
 * person turns them on here, and the operating system asks once.
 */

const KEY = 'genworx:notify';

interface TauriBridge {
  invoke(command: string, args?: Record<string, unknown>): Promise<unknown>;
}

function tauri(): TauriBridge | undefined {
  return (window as unknown as { __TAURI_INTERNALS__?: TauriBridge }).__TAURI_INTERNALS__;
}

/** Whether this device can show notifications at all. */
export function canNotify(): boolean {
  return tauri() !== undefined || typeof Notification !== 'undefined';
}

/** Whether the person turned them on, on this device. */
export function notifyWanted(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on';
  } catch {
    return false;
  }
}

/** Asks the operating system (or the browser); true when notifications are allowed. */
async function askPermission(): Promise<boolean> {
  const bridge = tauri();
  if (bridge) {
    const state = await bridge
      .invoke('plugin:notification|request_permission')
      .catch(() => 'denied');
    return state === 'granted';
  }
  if (typeof Notification !== 'undefined') {
    return Notification.permission === 'granted'
      ? true
      : (await Notification.requestPermission()) === 'granted';
  }
  return false;
}

/** Turns them on (asking the operating system) or off; true when they are on. */
export async function setNotify(on: boolean): Promise<boolean> {
  const granted = on ? await askPermission() : true;
  try {
    if (on && granted) {
      localStorage.setItem(KEY, 'on');
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    // Storage blocked: the setting lasts only as long as this page.
  }
  return on && granted;
}

/** Whether webmail is what the person is looking at: then the page says it. */
function inFront(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/** Shows one through the desktop app or the browser. Never throws. */
function show(title: string, body: string, tag: string, onClick?: () => void): void {
  const bridge = tauri();
  if (bridge) {
    void bridge
      .invoke('plugin:notification|notify', { options: { title, body } })
      .catch(() => undefined);
    return;
  }
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const shown = new Notification(title, { body, tag });
      shown.onclick = () => {
        window.focus();
        onClick?.();
        shown.close();
      };
    } catch {
      // Some browsers only allow them from a service worker.
    }
  }
}

/**
 * Shows one, when the person wants them and is not looking at webmail
 * already. Never throws: a notification that cannot be shown is not an error.
 */
export function notifyNewMail(title: string, body: string): void {
  if (!notifyWanted() || inFront()) {
    return;
  }
  show(title, body, 'genworx-new-mail');
}

/**
 * Asked when the person sets an event's alert: the alert is the request, so
 * it does not wait for the new-mail setting. Only a click may ask a browser.
 */
export function askToNotifyReminders(): void {
  if (tauri() || (typeof Notification !== 'undefined' && Notification.permission === 'default')) {
    void askPermission().catch(() => undefined);
  }
}

/** An event reminder, when webmail is not in front; the page shows its own card. */
export function notifyReminder(
  title: string,
  body: string,
  tag: string,
  onClick: () => void,
): void {
  if (inFront()) {
    return;
  }
  show(title, body, tag, onClick);
}
